import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import FilterBarWithTabs, { ListViewMode } from '../shared/TailwindComponents/FilterBarWithTabs';
import Header from '../shared/TailwindComponents/Header';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import { PiBriefcaseLight, PiUserPlusLight, PiShareNetworkLight, PiSpinnerGapLight, PiPlusLight, PiHourglassLight, PiClockCounterClockwiseLight, PiArchiveLight } from 'react-icons/pi';
import { convertLongDateWithTranslation, convertShortDateWithTranslation } from '../shared/DateConverter';
import Button from '../shared/TailwindComponents/Button';
import { hideModal, showModal } from '../../store/actions/modalActions';
import clsx from 'clsx';
import { getStatusColor, formatDateTime } from '../../utils/functions';
import {
  addMatterCollaborators,
  archiveMatter,
  fetchMatters,
  fetchMatterBoardStages,
  updateMatterBoardStage,
  MATTER_BOARD_STAGES,
} from '../../store/actions/Matters/MatterActions';
import StageBoard from '../shared/board/StageBoard';
import MatterCard from './MatterCard';
import { formatCurrency } from '../../utils/formatCurrency';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import MatterForm from './MatterForm';
import { ShareUser } from '../shared/ShareAccessManager';
import { fetchMatterClientRoles, fetchMatterCollaboratorRoles } from '../../store/actions/DropdownActions';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import Flag from '../shared/TailwindComponents/Flag';
import ResourceTrash from '../RecycleBin/ResourceTrash';
import { Tooltip } from '@mui/material';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { FeatureTour, mattersWelcomeTourSteps, mattersOverviewTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { getJurisdictionDisplay } from '../../utils/jurisdictionFlag';

import ActivityDrawer from '../shared/activity/ActivityDrawer';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';

/* `statusToColorMap` lived here to colour the card's stage badge. That badge
   is gone with the rest of the stage UI, and its stage keys (Discovery,
   Negotiation, Trial, Intake) had no other caller — the Status column colours
   through `getStatusColor`, which reads the org's own status list. */

/**
 * Jurisdiction → flag + label.
 *
 * Re-exported from `utils/jurisdictionFlag` so the several files that import
 * it from here keep working. The nine-entry passthrough map this replaced is
 * what left the Bulgarian flag blank — see that module's header.
 */
export { getJurisdictionDisplay };

// Portal tooltip component that renders outside table
const TooltipPortal: React.FC<{
  content: string;
  triggerRect: DOMRect;
  isVisible: boolean;
}> = ({ content, triggerRect, isVisible }) => {
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom');
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible || !tooltipRef.current) return;

    requestAnimationFrame(() => {
      const tooltipHeight = tooltipRef.current?.offsetHeight || 0;
      const spaceBelow = window.innerHeight - triggerRect.bottom - 10;

      if (spaceBelow < tooltipHeight && triggerRect.top > tooltipHeight + 10) {
        setTooltipPosition('top');
      } else {
        setTooltipPosition('bottom');
      }
    });
  }, [isVisible, triggerRect]);

  const tooltipTop = tooltipPosition === 'bottom'
    ? triggerRect.bottom + 8
    : triggerRect.top - (tooltipRef.current?.offsetHeight || 0) - 8;

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className={clsx(
        'fixed pointer-events-none z-50 transition-all duration-200 ease-out',
        isVisible
          ? 'opacity-100 translate-y-0 visible'
          : 'opacity-0 pointer-events-none'
      )}
      style={{
        left: `${Math.max(8, Math.min(triggerRect.left, window.innerWidth - 352))}px`,
        top: `${tooltipTop}px`,
        transform: isVisible
          ? 'translateY(0)'
          : tooltipPosition === 'bottom'
            ? 'translateY(-4px)'
            : 'translateY(4px)',
      }}
    >
      <div className="glass text-fg-strong text-xs rounded-lg px-4 py-3 max-w-sm shadow-xl">
        {content}
      </div>
    </div>,
    document.body
  );
};

// Viewport-aware tooltip component for Matter Name
const MatterNameCell: React.FC<{
  matterTitle: string;
  matterType: string;
  matterDescription: string;
  isProcessing: boolean;
  isCompleted: boolean;
  isNew: boolean;
  isFailed: boolean;
  isPending: boolean;
  isQueued: boolean;
  jurisdictionData: any;
  t: any;
  liveStep?: string;
  liveMessage?: string;
  livePercent?: number;
}> = ({
  matterTitle,
  matterType,
  matterDescription,
  isProcessing,
  isCompleted,
  isNew,
  isFailed,
  isPending,
  isQueued,
  jurisdictionData,
  t,
  liveStep,
  liveMessage,
  livePercent,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  const handleMouseEnter = useCallback(() => {
    // Intent delay: only surface the tooltip after a sustained 3s hover
    hoverTimerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        setTriggerRect(triggerRef.current.getBoundingClientRect());
        setIsVisible(true);
      }
    }, 3000);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsVisible(false);
  }, []);

  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  return (
    <>
      <div
        className="flex items-center gap-2 relative w-full min-w-0 pr-4"
        style={{ opacity: isFailed ? 0.6 : 1 }}
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {jurisdictionData && isCompleted && (
          <Flag countryCode={jurisdictionData.countryCode} code={jurisdictionData.code} size="sm" />
        )}
        <div className="flex-1 min-w-0 overflow-hidden">
          {isProcessing ? (
            // Processing / Running: no title — only show live step info
            <div className="flex items-center gap-2">
              <MiniLoadingToCheckmark isLoading={true} />
              <div className="flex-1 min-w-0">
                {liveStep ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-info-600 dark:text-info-400 font-medium truncate">
                        {liveStep.replace(/_/g, ' ')}
                      </span>
                    </div>
                    {liveMessage && (
                      <div className="text-xs text-fg-muted truncate mt-0.5">
                        {liveMessage}
                      </div>
                    )}
                    {livePercent != null && livePercent > 0 && (
                      <div className="mt-1 h-1 w-full bg-surface-sunken rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-info-500 to-info-700 transition-all duration-700 ease-in-out rounded-full"
                          style={{ width: `${livePercent}%` }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-sm text-fg-muted italic">
                      {t('Processing matter...')}
                    </div>
                    <div className="h-3 w-2/3 bg-surface-sunken rounded mt-1 animate-fadePulse"></div>
                  </>
                )}
              </div>
            </div>
          ) : isFailed ? (
            // Failed: show failure indicator, no title
            <div className="flex items-center gap-2">
              <div className="text-sm text-danger-500 dark:text-danger-400 italic">
                {t('Processing failed')}
              </div>
            </div>
          ) : !isCompleted ? (
            // Pending / Queued / any other non-completed: metadata not ready
            <div className="flex items-center gap-2">
              <PiHourglassLight className="h-4 w-4 text-warning-400 animate-pulse flex-shrink-0" />
              <div>
                <div className="text-sm text-fg-muted italic">
                  {isPending ? t('Waiting to be queued...') : isQueued ? t('Queued for processing...') : t('Preparing...')}
                </div>
                <div className="text-xs text-fg-muted mt-0.5">
                  {t('Details will appear once processing completes')}
                </div>
              </div>
            </div>
          ) : (
            // Completed: show full title + type
            <>
              <div className="text-fg-strong truncate" title={matterTitle}>
                {matterTitle}
              </div>
              <div className="text-xs text-fg-muted mt-1 truncate">
                {matterType || t('No type specified')}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
          {isNew && isCompleted && <Badge color="green">{t('New')}</Badge>}
        </div>
      </div>

      {/* Portal-rendered tooltip — only for completed matters */}
      {isCompleted && matterDescription && triggerRect && (
        <TooltipPortal content={matterDescription} triggerRect={triggerRect} isVisible={isVisible} />
      )}
    </>
  );
};

const Matters: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const locationState = location.state as any;

  const allMatters = useSelector((state: RootState) => state.matters.matters || []);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);

  const [isMattersLoading, setIsMattersLoading] = useState(false);
  const [isAddingMatter, setIsAddingMatter] = useState(locationState?.isAddingMatter || false);
  const isAddingMatterRef = useRef(isAddingMatter);
  const historyRef = useRef(history);
  const [viewMode, setViewMode] = useState<ListViewMode>('table');
  /**
   * Board columns. Seeded with the contract's four so the board paints before
   * `/matters/stages` answers (and still works if it never does).
   */
  const [boardStages, setBoardStages] = useState<string[]>([...MATTER_BOARD_STAGES]);
  /**
   * Which set the list is showing. `false` is the active list; `true` is the
   * Archive — the ONLY place a matter can be permanently deleted from.
   *
   * Archiving IS the recycle-bin soft delete (see `archiveMatter`), so the
   * Archive is not a second list to maintain: it is `ResourceTrash` reading
   * `/recycle-bin?entity_type=matter`, the same source and the same component
   * the trash view uses. One flag drives both entry points — the Active /
   * Archived scope switch and the recycle-bin toggle — so they cannot disagree
   * about what is on screen.
   */
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  /*
    The page no longer scrolls as a whole — the list owns the only scroller
    (see the height contract on the root element below) — so the header's
    old scroll-to-shrink sticky behaviour, and the scrollTop state that drove
    it, have nothing left to react to. The toolbar is simply pinned.
  */
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedMatters, setSelectedMatters] = useState<string[]>([]);
  const [totalMatters, setTotalMatters] = useState(0);
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const collaboratorRoleOptions = useSelector((state: RootState) => state.dropdown.dropdowns.collaboratorRoles || []);

  // ── Feature tour for first-time visitors ──
  const isEmptyState = !isMattersLoading && allMatters.length === 0 && !isAddingMatter && !showTrash;
  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
    startTour: startWelcomeTour,
    hasCompleted: hasCompletedWelcomeTour,
  } = useFeatureTour({
    tourId: 'matters-welcome',
    steps: mattersWelcomeTourSteps,
    readyToShow: isEmptyState,
    autoStartDelay: 800,
  });

  // ── Feature tour for returning visitors with matters ──
  const hasMatters = !isMattersLoading && allMatters.length > 0 && !isAddingMatter && !showTrash;
  const {
    isRunning: isOverviewTourRunning,
    handleJoyrideCallback: handleOverviewTourCallback,
  } = useFeatureTour({
    tourId: 'matters-overview',
    steps: mattersOverviewTourSteps,
    readyToShow: hasMatters,
    autoStartDelay: 800,
  });

  // Audit log side panel state
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditMatter, setAuditMatter] = useState<{ id: string; name: string } | null>(null);

  // Keep refs in sync for use inside stable callbacks
  useEffect(() => { isAddingMatterRef.current = isAddingMatter; }, [isAddingMatter]);
  useEffect(() => { historyRef.current = history; }, [history]);

  // ── PubSub: track active processing matters ──
  // Live processing state per matter UUID (step, message, percent)
  interface LiveProcessingState {
    step?: string;
    message?: string;
    percent?: number;
    status?: string;
  }
  const [processingStates, setProcessingStates] = useState<Record<string, LiveProcessingState>>({});
  const activeGroupsRef = useRef<Set<string>>(new Set());
  const correlationToUuidRef = useRef<Map<string, string>>(new Map());
  const pubsubConnectedRef = useRef(false);
  const fetchRef = useRef<() => void>(() => {});
  const refetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  /** The pending "open the matter that just finished" hop. Cleared on unmount. */
  const navTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  /*
    Callback passed to MatterForm — it tells us the PubSub group so the list
    page can keep listening after MatterForm unmounts.

    The correlation → uuid mapping matters as much as the group name: a matter
    created in this session is not in any fetched page yet, so
    `joinActiveProcessingGroups` has never seen it, and without this entry a
    completion frame that carries only a correlation id cannot be resolved to
    the matter it finished.
  */
  const handleMatterGroupJoined = useCallback(
    (groupName: string, info?: { matterUuid?: string; correlationId?: string }) => {
      activeGroupsRef.current.add(groupName);
      if (info?.correlationId && info.matterUuid) {
        correlationToUuidRef.current.set(info.correlationId, info.matterUuid);
      }
    },
    [],
  );

  // ── Handle incoming PubSub messages — route to correct matter's live state ──
  const handleListPubSubMessage = useCallback(
    (raw: any, eventName?: string) => {
      const event = Array.isArray(raw) ? raw[0] : raw || {};
      const innerData = event.data || {};
      const data = { ...event, ...innerData };

      const correlationId = data.correlationId || data.correlation_id || data.CorrelationID || null;
      const matterUuid = data.matter_uuid || data.matterUuid || data.MatterUUID || null;

      const rawStatus = data.status ?? '';
      const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : String(rawStatus).toLowerCase();
      const subType = data.sub_type || data.event_name || data.eventType || data.event_type || null;
      const stepName = data.step_name || data.stepName || data.step || null;
      const message = data.message || '';
      const progressPercent = data.progress_percent;

      // Resolve which matter UUID this event belongs to
      let targetUuid = matterUuid;
      if (!targetUuid && correlationId) {
        targetUuid = correlationToUuidRef.current.get(correlationId);
      }

      const isCompletion = status === 'completed' || subType === 'complete' || data.event_name === 'ai_processing_complete';
      const isFailure = status === 'failed' || data.error;

      // Skip technical messages without useful data
      const isTechnicalMessage = (stepName === 'system' || subType === 'system' || subType === 'connected' || subType === 'disconnected') && !message;
      if (isTechnicalMessage) return;

      console.log('[Matters PubSub]', { eventName, status, subType, stepName, message, correlationId, targetUuid });

      // Update live processing state for this matter
      if (targetUuid && !isCompletion && !isFailure) {
        setProcessingStates(prev => ({
          ...prev,
          [targetUuid]: {
            step: stepName || prev[targetUuid]?.step,
            message: message || prev[targetUuid]?.message,
            percent: progressPercent ?? prev[targetUuid]?.percent,
            status: status || prev[targetUuid]?.status,
          },
        }));
      }

      if (isCompletion || isFailure) {
        console.log('[Matters PubSub] Detected completion/failure — scheduling refetch');

        // Clear live state for this matter
        if (targetUuid) {
          setProcessingStates(prev => {
            const next = { ...prev };
            delete next[targetUuid];
            return next;
          });
        }

        // Leave the group since processing is done
        if (correlationId) {
          const groupName = `matter_${correlationId}`;
          const multiHubService = getMultiHubSocketService();
          multiHubService.emit('ai_processing', 'leave', { group: groupName });
          activeGroupsRef.current.delete(groupName);
        }

        /*
          The fallback route to the finished matter, for the run whose form
          has already gone (closed, or the page reloaded mid-run). MatterForm
          navigates itself while it is up; this covers the rest.

          The timer is held and cleared on unmount. Left loose it fired half a
          second after the page had been replaced, pushing from a history it
          no longer belonged to — an update from a dead component, reported
          against `BrowserRouter`. It also refuses to build a path out of a
          uuid it does not have: `/matters/undefined` is a route that matches
          and then fails to load anything.
        */
        const completedMatterUuid = targetUuid || matterUuid;
        if (isCompletion && completedMatterUuid && isAddingMatterRef.current) {
          if (navTimerRef.current) clearTimeout(navTimerRef.current);
          navTimerRef.current = setTimeout(() => {
            navTimerRef.current = null;
            if (!mountedRef.current) return;
            historyRef.current.replace(`/matters/${completedMatterUuid}`);
          }, 500);
          return;
        }

        // Debounced refetch — if multiple matters complete in quick succession,
        // only refetch once after a short delay
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => {
          fetchRef.current();
        }, 1500);
      }
    },
    []
  );

  // ── Connect to PubSub hub once on mount, clean up on unmount ──
  useEffect(() => {
    let mounted = true;
    // Re-armed per mount: StrictMode tears the first one down in development.
    mountedRef.current = true;

    const connectPubSub = async () => {
      const multiHubService = getMultiHubSocketService();
      const connected = await multiHubService.connectToHub('ai_processing');
      if (connected && mounted) {
        pubsubConnectedRef.current = true;
        multiHubService.on('ai_processing', '*', handleListPubSubMessage);
      }
    };

    connectPubSub();

    return () => {
      mounted = false;
      mountedRef.current = false;
      const multiHubService = getMultiHubSocketService();

      // Leave all tracked groups
      for (const group of activeGroupsRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group });
      }
      activeGroupsRef.current.clear();
      correlationToUuidRef.current.clear();

      multiHubService.off('ai_processing', '*', handleListPubSubMessage);
      multiHubService.disconnectHub('ai_processing');
      pubsubConnectedRef.current = false;

      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
    };
  }, [handleListPubSubMessage]);

  // ── Join PubSub groups for all active processing matters ──
  // This runs after each fetch to pick up new processing matters
  const joinActiveProcessingGroups = useCallback((matters: any[]) => {
    if (!pubsubConnectedRef.current) return;
    const multiHubService = getMultiHubSocketService();

    const activeStatuses = new Set(['processing', 'running', 'queued', 'pending']);

    matters.forEach((matter: any) => {
      const status = matter.processing_status || 'pending';
      const correlationId = matter.processing_correlation_id;
      const matterUuid = matter.uuid || matter.MatterID;

      if (activeStatuses.has(status) && correlationId) {
        const groupName = `matter_${correlationId}`;

        // Map correlation ID → matter UUID for routing events
        correlationToUuidRef.current.set(correlationId, matterUuid);

        if (!activeGroupsRef.current.has(groupName)) {
          console.log(`[Matters PubSub] Joining group ${groupName} for matter ${matterUuid} (status: ${status})`);
          multiHubService.emit('ai_processing', 'join', { group: groupName });
          activeGroupsRef.current.add(groupName);
        }
      }
    });
  }, []);

  const { checkSectionGate } = useLicenseGate();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('add') === 'true') {
      if (checkSectionGate('matters')) {
        setIsAddingMatter(true);
      } else {
        // Remove query param so user isn't stuck
        history.replace('/matters');
      }
    } else {
      setIsAddingMatter(false);
    }
  }, [location]);

  const fetchInitialMatters = useCallback(async () => {
    setIsMattersLoading(true);
    setPage(1);
    const resp = await dispatch(fetchMatters({
      searchTerm: searchQuery,
      filters: selectedFilters,
      sortBy,
      sortOrder,
      page: 1,
      page_size: 50,
    }));

    const { matters, currentPage, total_matters } = resp;
    setPage(currentPage);
    const nextPageSize = matters.length;
    const totalPages = Math.ceil(total_matters / 50);
    const isLastPage = currentPage >= totalPages || nextPageSize < 50;
    setHasMore(!isLastPage);
    setTotalMatters(total_matters);
    setSelectedMatters([]);
    setIsMattersLoading(false);

    // Join PubSub groups for any actively processing matters
    joinActiveProcessingGroups(matters);
  }, [dispatch, searchQuery, selectedFilters, sortBy, sortOrder, joinActiveProcessingGroups]);

  // Keep fetchRef in sync so PubSub callback always calls latest version
  useEffect(() => {
    fetchRef.current = fetchInitialMatters;
  }, [fetchInitialMatters]);

  const loadMoreMatters = useCallback(async () => {
    setIsMattersLoading(true);
    const next = page + 1;

    const resp = await dispatch(fetchMatters({
      searchTerm: searchQuery,
      filters: selectedFilters,
      sortBy,
      sortOrder,
      page: next,
      page_size: 50,
    }));

    const { matters, currentPage, total_matters } = resp;
    setPage(currentPage);
    const nextPageSize = matters.length;
    const totalPages = Math.ceil(total_matters / 50);
    const isLastPage = currentPage >= totalPages || nextPageSize < 50;
    setHasMore(!isLastPage);
    setIsMattersLoading(false);
  }, [dispatch, page, searchQuery, selectedFilters, sortBy, sortOrder]);

  useEffect(() => {
    setPage(1);
    // dispatch(fetchMatterClientRoles())
    // dispatch(fetchMatterCollaboratorRoles())
    setSelectedMatters([]);
    fetchInitialMatters();
  }, [searchQuery, selectedFilters, sortBy, sortOrder, fetchInitialMatters]);

  // ── Refetch whenever the user navigates back to the list view ──
  const prevIsAddingRef = useRef(isAddingMatter);
  useEffect(() => {
    // Detect transition from adding → list (form closed / navigated back)
    if (prevIsAddingRef.current && !isAddingMatter) {
      // Safety: re-register the list PubSub handler in case MatterForm's unmount
      // somehow removed it (the targeted off() should prevent this, but belt-and-suspenders).
      if (pubsubConnectedRef.current) {
        const multiHubService = getMultiHubSocketService();
        // Remove first to avoid duplicate registrations, then re-add
        multiHubService.off('ai_processing', '*', handleListPubSubMessage);
        multiHubService.on('ai_processing', '*', handleListPubSubMessage);
      }
      fetchInitialMatters();
    }
    prevIsAddingRef.current = isAddingMatter;
  }, [isAddingMatter, fetchInitialMatters, handleListPubSubMessage]);

  // Refetch when navigating back to /matters from a sub-page (location changes)
  const prevLocationRef = useRef({ key: location.key, pathname: location.pathname });
  useEffect(() => {
    const prev = prevLocationRef.current;
    const isMattersListPage = location.pathname === '/matters';
    const locationChanged = prev.key !== location.key || prev.pathname !== location.pathname;
    const wasOnSubPage = prev.pathname !== '/matters' && prev.pathname.startsWith('/matters/');

    // Refetch if: navigating back to /matters list from a different page/key
    if (isMattersListPage && locationChanged && !isAddingMatter) {
      fetchInitialMatters();
    }

    prevLocationRef.current = { key: location.key, pathname: location.pathname };
  }, [location.key, location.pathname, isAddingMatter, fetchInitialMatters]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterName]: selectedValues,
    }));
  };

  const handleSortChange = (newSortBy: string, newSortOrder?: 'asc' | 'desc') => {
    // Handle both direct calls from table headers (with two params)
    // and calls from FilterBarWithTabs (with encoded value like "field_order")
    if (newSortOrder) {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
    } else {
      // Decode the sort value from FilterBarWithTabs (format: "field_order")
      const parts = newSortBy.split('_');
      const order = parts.pop() as 'asc' | 'desc';
      const field = parts.join('_');
      setSortBy(field);
      setSortOrder(order);
    }
  };

  const handleAddMatterClick = () => {
    setIsAddingMatter(true);
    history.push('/matters?add=true');
  };

  const handleAddMatterClose = () => {
    setIsAddingMatter(false);
    history.push('/matters');
  };

  const handleMatterClick = (matterItem: any) => {
    // Only allow clicking into matters that have completed processing
    const processingStatus = matterItem.processing_status;
    if (processingStatus && processingStatus !== 'completed') {
      // Show a subtle feedback message depending on state
      if (processingStatus === 'processing' || processingStatus === 'running') {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('This matter is still being processed. Please wait until processing completes.'),
          modalType: 'info',
          duration: 3000,
        }));
      } else if (processingStatus === 'failed') {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('This matter failed to process. Try restarting the process.'),
          modalType: 'warning',
          duration: 3000,
        }));
      }
      return;
    }
    // Use UUID from matter data for navigation (matterId is the numeric ID, uuid is the UUID)
    const matterUuid = matterItem.uuid || matterItem.MatterID;
    history.push(`/matters/${matterUuid}`);
  };

  const handleRestartMatter = (matterItem: any, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    dispatch(showModal({
      type: 'confirmation',
      message: t('Restart Matter Processing'),
      subMsg: t('This will restart the AI processing pipeline for this matter. Any in-progress analysis will be reset.'),
      onConfirm: async () => {
        try {
          // TODO: Call the restart API endpoint here
          // await dispatch(restartMatterProcessing(matterItem.id));
          dispatch(showFeedbackModal({
            modalType: 'success',
            message: t('Matter processing restarted successfully.'),
            showModal: true,
            duration: 3000,
          }));
          fetchInitialMatters();
        } catch (error) {
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Failed to restart matter processing.'),
            showModal: true,
            duration: 3000,
          }));
        }
        dispatch(hideModal());
      },
      onCancel: () => { },
      showModal: true,
    }));
  };

  /*
    No 'MatterStage' filter — stage is gone from the matter UI, so filtering
    by something the list no longer shows would be filtering blind.

    Nothing needs unpicking from the query. `selectedFilters` is keyed by each
    filter's own `id`, starts empty on every mount (plain `useState`, no URL or
    storage rehydration) and is only ever written by `handleFilterChange` for a
    filter that is on screen — so with this definition gone, the key can no
    longer be created. The stage columns and `advanceMatterStage` are untouched
    on the API.
  */
  const filters = [
    {
      id: 'status',
      name: t('Status'),
      options: [
        { value: 'open', label: t('Open') },
        { value: 'active', label: t('Active') },
        { value: 'closed', label: t('Closed') },
      ],
    },
  ];

  const sortOptions = [
    { value: 'created_at_asc', label: t('Created At (Oldest First)'), current: sortBy === 'created_at' && sortOrder === 'asc' },
    { value: 'created_at_desc', label: t('Created At (Newest First)'), current: sortBy === 'created_at' && sortOrder === 'desc' },
    { value: 'matter_name_asc', label: t('Matter Name (A-Z)'), current: sortBy === 'matter_name' && sortOrder === 'asc' },
    { value: 'matter_name_desc', label: t('Matter Name (Z-A)'), current: sortBy === 'matter_name' && sortOrder === 'desc' },
  ];

  const handleClearAllFilters = () => {
    setSelectedFilters({});
    setSearchQuery('');
    setSortBy('');
    setSortOrder('asc');
  };

  /**
   * ACTIVE ⟷ ARCHIVE — a SCOPE, not a filter.
   *
   * It selects which set is listed, and the row actions change with it:
   * Archive on the left, Restore and Delete permanently on the right. It used
   * to own a whole line above the filter bar as a large sticky pill row; it
   * now sits beside the view switch, and is passed through the filter bar's
   * dedicated `scope` prop rather than folded into `filters` — so
   * "Clear all filters" still cannot move the user to a list they did not ask
   * to be in.
   */
  const scopeSwitch = useMemo(() => ({
    value: showTrash ? 'archived' : 'active',
    label: t('Matter scope'),
    options: [
      { value: 'active', label: t('Active'), icon: <PiBriefcaseLight className="h-3 w-3" /> },
      { value: 'archived', label: t('Archived'), icon: <PiArchiveLight className="h-3 w-3" /> },
    ],
    onChange: (value: string) => {
      const archived = value === 'archived';
      if (showTrash === archived) return;
      // A selection made in one set means nothing in the other.
      setSelectedMatters([]);
      // Coming back to Active, the archived matter has to leave that list.
      if (!archived) fetchInitialMatters();
      setShowTrash(archived);
    },
  }), [showTrash, t, fetchInitialMatters]);

  // ── Board: stage columns, and the drag-and-drop move ──
  useEffect(() => {
    let cancelled = false;
    dispatch(fetchMatterBoardStages()).then((stages) => {
      if (!cancelled && stages.length > 0) setBoardStages(stages);
    });
    return () => { cancelled = true; };
  }, [dispatch]);

  const boardColumns = useMemo(
    () => boardStages.map((stage) => ({ id: stage, label: t(stage) })),
    [boardStages, t]
  );

  const handleBoardMove = useCallback(
    async (matterItem: any, stage: string) => {
      const matterUuid = matterItem.uuid || matterItem.MatterID;
      return dispatch(updateMatterBoardStage(matterUuid, stage));
    },
    [dispatch]
  );

  /**
   * ARCHIVE — the destructive action the active list offers, and the only one.
   *
   * Soft: the matter leaves the active list with everything intact and is
   * restorable from the Archive. The hard delete this replaces asked, from a
   * row menu, for a decision that could not be taken back.
   */
  const handleArchiveMatters = (matterIds: string[]) => {
    if (matterIds.length === 0) {
      dispatch(showFeedbackModal({
        modalType: 'warning',
        message: t('Please select at least one matter.'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }
    const many = matterIds.length > 1;
    dispatch(showModal({
      type: 'confirmation',
      title: many ? t('Archive these matters?') : t('Archive this matter?'),
      message: many
        ? t('They will be hidden from the active list. Nothing is deleted.')
        : t('It will be hidden from the active list. Nothing is deleted.'),
      subText: t('You can restore it at any time from the Archive.'),
      icon: <PiArchiveLight className="w-6 h-6 text-warning-600" />,
      variant: 'warning',
      confirmLabel: t('Archive'),
      onConfirm: async () => {
        const results = await Promise.all(
          matterIds.map((id) => dispatch(archiveMatter(id) as any))
        );
        dispatch(hideModal());
        if (results.some(Boolean)) {
          setSelectedMatters([]);
          fetchInitialMatters();
        }
      },
      onClose: () => dispatch(hideModal()),
      showModal: true,
    }));
  };

  /*
    RESTORE and PERMANENT DELETE are not here. Both act on an ARCHIVED matter,
    and the Archive is `ResourceTrash` — it owns those two actions against
    `/recycle-bin/restore` and `/recycle-bin/permanent-delete`, feeds its bulk
    actions up through `onBulkActionsChange`, and confirms the destroy through
    `RecycleBinDeleteConfirmation`. A second implementation here could only
    drift from it.

    (That confirmation is a plain Cancel/Delete pair. This comment used to
    describe it as a type-to-confirm, which it has never been — the app's only
    typed confirmation is `shared/TypeToConfirm`, added for revoking a person's
    access. Worth pointing that gap at the permanent delete of a matter.)
  */

  const handleAddCollaborator = (matterId: string, collaborators: ShareUser[]) => {
    dispatch(showModal({
      showModal: true,
      type: 'giveAccess',
      onSave: async (nextShared: ShareUser[]) => {
        /*
          `Permission: 'write'` was not a level the backend knows — it accepts
          view|edit|admin, per collaborator, off each collaborator object. The
          picker already records a per-person access, so that is what goes up;
          'edit' is the fallback for a row the picker left unset.
        */
        await dispatch(addMatterCollaborators({
          MatterID: matterId,
          Collaborators: nextShared,
          Permission: 'edit',
          Roles: ['Attorney'],
        }));
        fetchInitialMatters(); 
        dispatch(hideModal());
      },
      message: t('Add Collaborator'),
      subMsg: t('Select users to add as collaborators to this matter.'),
      employees: [],
      initialShared: collaborators,
      roles: collaboratorRoleOptions,
      icon: <PiShareNetworkLight className="h-5 w-5 text-primary-600" />,
    }));
  };

  /*
    Bulk actions for the ACTIVE list. In the Archive the toolbar takes its
    actions from `trashBulkActions` instead — Restore and Delete permanently,
    published by ResourceTrash — so this list contributes nothing there.
  */
  const matterActions = showTrash
    ? []
    : [
      {
        label: t('Archive Matters'),
        onClick: (matterIds: string[]) => handleArchiveMatters(matterIds),
        icon: <PiArchiveLight className="h-4 w-4" />,
      },
    ];

  const tableActions = [
    // {
    //   label: t('Add Collaborator'),
    //   onClick: (row: any) => handleAddCollaborator(row.id,row.matterCollaborators),
    //   icon: <PiUserPlusLight className="h-4 w-4" />,
    // },
    {
      label: t('Activity Log'),
      onClick: (row: any) => {
        const matter = allMatters.find((m: any) => (m.uuid || m.MatterID) === row.id);
        const name = matter?.title || matter?.MatterName || '';
        setAuditMatter({ id: row.id, name });
        setShowAuditLog(true);
      },
      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
    },
    /*
      NO HARD DELETE ON AN ACTIVE MATTER. This table only ever shows the
      active list, so Archive is the whole of what a row offers; Restore and
      Delete permanently belong to the Archive, and live on ResourceTrash's
      own rows.
    */
    {
      label: t('Archive Matter'),
      onClick: (row: any) => handleArchiveMatters([row.id]),
      icon: <PiArchiveLight className="h-4 w-4" />,
      isDestructive: true,
    },
  ];

  const columns = useMemo(
    () => [
      'Select',
      'Name',
      // 'Stage' is gone from the UI — see the cell block below.
      'Status',
      // 'Creation Status',
      'Revenue',
      'Created At',
      'Clients',
      'Access',
      'Actions',
    ],
    []
  );

  /*
    Revenue is NOT sortable: `/matters/view` sorts on its own columns and does
    not accept a revenue key, and sorting one page of a paginated list on the
    client would silently reorder 50 of N rows and call it "sorted by revenue".
    Add it here the moment the endpoint can sort on it.
  */
  const sortableColumns = ['Name', 'Created At'];

  const tableData = useMemo(() => {
    const now = new Date();
    return allMatters.map((matterItem: any) => {
      const createdDate = new Date(matterItem.created_at);
      const isNew = (now.getTime() - createdDate.getTime()) / 3600000 < 24;
      const processingStatus = matterItem.processing_status || 'pending';
      const isFailed = processingStatus === 'failed';
      const isProcessing = processingStatus === 'processing' || processingStatus === 'running';
      const isCompleted = processingStatus === 'completed' || processingStatus === 'open';
      const isPending = processingStatus === 'pending';
      const isQueued = processingStatus === 'queued';
      const isClickable = isCompleted;

      const documentCount = matterItem.DocumentCount || 0;
      const documentText = documentCount > 0 ? `${documentCount} document${documentCount > 1 ? 's' : ''}` : t('No documents');

      // Use UUID for ID if available, otherwise fall back to numeric ID
      const matterUuid = matterItem.uuid || matterItem.MatterID;

      // Get title from AI extraction or fallback to MatterName
      const matterTitle = matterItem.title || matterItem.MatterName || `Matter ${matterItem.MatterID}`;
      const matterType = matterItem.type || '';
      const matterDescription = matterItem.description || '';
      const jurisdictionData = getJurisdictionDisplay(matterItem.jurisdiction);

      // Get live processing state from PubSub
      const liveState = processingStates[matterUuid];

      return {
        id: matterUuid,
        matterTitle: matterTitle,
        processing_status: processingStatus,
        _isClickable: isClickable,
        'Name': (
          <MatterNameCell
            matterTitle={matterTitle}
            matterType={matterType}
            matterDescription={matterDescription}
            isProcessing={isProcessing}
            isCompleted={isCompleted}
            isNew={isNew}
            isFailed={isFailed}
            isPending={isPending}
            isQueued={isQueued}
            jurisdictionData={jurisdictionData}
            t={t}
            liveStep={liveState?.step}
            liveMessage={liveState?.message}
            livePercent={liveState?.percent}
          />
        ),
        /*
          NO STAGE COLUMN.

          Stage was removed from the matter UI: it duplicated Status for most
          matters and the two drifted apart wherever it did not. The
          `stage_name` / `MatterStage` fields and `advanceMatterStage` are
          untouched on the API and in the payloads — this is display only.
        */
        'Status': !isCompleted ? (
          <span className="text-xs text-fg-muted italic">—</span>
        ) : (
          <Badge className='capitalize' color={getStatusColor(matterItem.status_name) as BadgeColor}>
            {matterItem.status_name || t('No status')}
          </Badge>
        ),
        // 'Creation Status': (
        //   <div className="flex items-center gap-2">
        //     <Badge
        //       className='capitalize'
        //       color={processingStatusToColorMap[processingStatus]}
        //       tooltip={isCompleted && matterItem.processing_completed_at ? `${t('Completed')}: ${formatDateTime(matterItem.processing_completed_at)}` : ''}
        //       icon={
        //         isProcessing ? <BrandLoader size="xs" /> :
        //         (isPending || isQueued) ? <PiClockLight className="text-warning-400" /> :
        //         undefined
        //       }
        //     >
        //       {isProcessing ? t('Processing') :
        //        isQueued ? t('Queued') :
        //        isPending ? t('Pending') :
        //        processingStatus}
        //     </Badge>
        //     {isFailed && (
        //       <Button
        //         variant="tertiary"
        //         size="small"
        //         disabled
        //         onClick={(e) => handleRestartMatter(matterItem, e)}
        //         tooltip={t('Retry processing this matter')}
        //         className="opacity-70 hover:opacity-100 transition-opacity"
        //       >
        //         {t('Retry')}
        //       </Button>
        //     )}
        //   </div>
        // ),
        /*
          REVENUE — right-aligned tabular figures, formatted in the matter's
          OWN currency. Never summed or converted across matters: two matters
          priced in different currencies have no common total.

          `null` is "we could not price this" and reads as a muted dash; a real
          0.0 is a fact and formats as money. Collapsing the two would report a
          lookup failure as "this matter earned nothing".
        */
        'Revenue': !isCompleted || matterItem.revenue == null ? (
          <span className="block text-right text-sm text-gray-400 dark:text-gray-500">—</span>
        ) : (
          <span
            className="block text-right text-sm font-medium tabular-nums text-gray-700 dark:text-gray-200"
            title={`${matterItem.revenue} ${(matterItem.revenue_currency || '').toUpperCase()}`.trim()}
          >
            {formatCurrency(matterItem.revenue, matterItem.revenue_currency || 'usd')}
          </span>
        ),
        'Created At': formatDateTime(matterItem.created_at),
        'Clients': matterItem.matter_clients?.clients?.length ? (
          <div className="flex items-center">
            {matterItem.matter_clients.clients.slice(0, 4).map((client: any, idx: number) => (
              <div
                key={client.client_uuid}
                className={clsx(
                  'relative rounded-full',
                  idx > 0 && 'ml-[-0.5rem]'
                )}
              >
                <Tooltip title={client.name || t('Unknown Client')} enterDelay={3000} enterNextDelay={3000}>
                  <div
                    className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-medium cursor-pointer hover:bg-primary-600 transition-colors"
                    onClick={() => history.push(`/contacts/${client.client_uuid}`)}
                  >
                    {(client.name || 'U')[0].toUpperCase()}
                  </div>
                </Tooltip>
              </div>
            ))}
            {matterItem.matter_clients.clients.length > 4 && (
              <span className="text-sm text-fg-muted ml-1">+{matterItem.matter_clients.clients.length - 4}</span>
            )}
          </div>
        ) : '-',
        'matterCollaborators': matterItem.Collaborators || [],
        'Access': (
          <div className="flex gap-3 items-center">
            <CollaboratorStack collaborators={matterItem.Collaborators || []} max={3} />
          </div>
        ),

      };
    });
  }, [allMatters, t, handleRestartMatter, processingStates]);

  /**
   * CARD VIEW — one `MatterCard` per matter (see that file for the hierarchy).
   *
   * The card used to be a generic `ListItem` carrying a run-on subtitle and an
   * undifferentiated label/value list, so nothing on it was scannable. The
   * card now owns its own layout and this is only the grid around it.
   */
  const loadMoreButton = hasMore ? (
    <div className="flex justify-center py-6">
      <Button
        variant="outline"
        size="small"
        onClick={loadMoreMatters}
        disabled={isMattersLoading}
        isLoading={isMattersLoading}
      >
        {isMattersLoading ? t('Loading...') : t('Load More')}
      </Button>
    </div>
  ) : null;

  const cardView = (
    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-4 pb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pt-2">
        {allMatters.map((matterItem: any) => {
          const matterUuid = matterItem.uuid || matterItem.MatterID;
          return (
            <MatterCard
              key={matterUuid}
              matter={matterItem}
              live={processingStates[matterUuid]}
              onClick={handleMatterClick}
            />
          );
        })}
        {isMattersLoading && allMatters.length === 0 && (
          <Skeleton variant="card" className="col-span-full" rows={6} />
        )}
        {allMatters.length === 0 && !isMattersLoading && (
          <EmptyState
            className="col-span-full"
            icon={<PiBriefcaseLight />}
            headline={t('You have no matters listed.')}
            subtext={t('Initiate by creating a new matter to organize details, track progress, and collaborate with your team.')}
            button={{ label: t('Create New Matter'), onClick: handleAddMatterClick }}
          />
        )}
      </div>
      {allMatters.length > 0 && loadMoreButton}
    </div>
  );

  /**
   * BOARD VIEW — the same matters as draggable cards under their stage.
   *
   * Drag persists through `PATCH /matters/<uuid>/stage`; a matter that has not
   * finished processing has no stage to speak of yet and is pinned in place.
   */
  const boardView = (
    <StageBoard
      columns={boardColumns}
      items={allMatters}
      dragType="matter-board-card"
      isLoading={isMattersLoading}
      getId={(matterItem: any) => matterItem.uuid || matterItem.MatterID}
      getStage={(matterItem: any) => matterItem.board_stage}
      canDrag={(matterItem: any) => {
        const status = matterItem.processing_status || 'pending';
        return status === 'completed' || status === 'open';
      }}
      onMove={handleBoardMove}
      renderCard={(matterItem: any) => (
        <MatterCard
          matter={matterItem}
          density="board"
          live={processingStates[matterItem.uuid || matterItem.MatterID]}
          onClick={handleMatterClick}
        />
      )}
      footer={loadMoreButton}
    />
  );

  return (
    <>
    {/*
      THE HEIGHT CONTRACT for this page.

      `MatterManagementPage` sets `isContentScrollable(false)`, so `<main>` is a
      definite-height `flex flex-col` box — but only if this page claims its
      share of it. Without `flex-1 min-h-0` the page is content-sized, every
      child `overflow-y-auto` resolves against `auto`, and the scrollers stack:
      the page grows AND an inner box scrolls, which is what let the deleted
      matters list scroll a screen and a half past its last row.

      There is exactly ONE scroller below this line per view, and it is the
      list's own.
    */}
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden relative">
      <div className="flex-shrink-0">
        {!isAddingMatter && (
          <div data-tour-id="matters-add-button">
            <Header
              actions={[
                {
                  label: t('Add Matter'),
                  icon: <PiPlusLight className='w-4 h-4' />,
                  onClick: handleAddMatterClick,
                },
              ]}
              subscriptionActive={subscriptionActive}
            />
          </div>
        )}
        {!isAddingMatter && (
          <div data-tour-id="matters-search-bar">
          <FilterBarWithTabs
            itemCount={allMatters.length}
            totalItemCount={totalMatters}
            itemCountLabel="matters"
            filters={filters}
            sortOptions={viewMode === 'table' ? [] : sortOptions}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onSortChange={viewMode === 'table' ? undefined : handleSortChange}
            viewMode={viewMode}
            setViewMode={setViewMode}
            viewModes={['grid', 'table', 'board']}
            scope={scopeSwitch}
            onSearchChange={handleSearchChange}
            sortValue={`${sortBy}_${sortOrder}`}
            onClearAllFilters={handleClearAllFilters}
            actions={[
              ...matterActions.map(action => ({
                label: action.label,
                onClick: () => action.onClick(selectedMatters),
                icon: action.icon,
              })),
              ...(showTrash ? trashBulkActions : []),
            ]}
            selectionCount={showTrash ? trashSelectedCount : (selectedMatters.length > 0 ? selectedMatters.length : undefined)}
            recycleBinEnabled
            recycleBinActive={showTrash}
            recycleBinLabel={t('Matters')}
            onRecycleBinToggle={(active) => { if (!active) fetchInitialMatters(); setShowTrash(active); }}
          />
          </div>
        )}
      </div>
      {showTrash ? (
        <div className="flex-1 min-h-0 flex flex-col">
          <ResourceTrash
            entityType="matter"
            entityLabel="matters"
            emptyIcon={<PiBriefcaseLight />}
            onItemRestored={() => { setPage(1); setHasMore(true); }}
            onBulkActionsChange={setTrashBulkActions}
            onSelectedCountChange={setTrashSelectedCount}
          />
        </div>
      ) : (
      <div className="flex-1 min-h-0 flex flex-col">
          {isAddingMatter ? (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <MatterForm
                onClose={handleAddMatterClose}
                clientId={locationState?.clientId}
                onGroupJoined={handleMatterGroupJoined}
              />
            </div>
          ) : (
            <div className="animate-fade-in flex-1 min-h-0 flex flex-col">
              {viewMode === 'grid' ? (
                cardView
              ) : viewMode === 'board' ? (
                boardView
              ) : (
                /*
                  The active list. Switching to the Archive swaps this whole
                  subtree for ResourceTrash above, so there is nothing here to
                  key by scope — the table is unmounted either way.
                */
                <div
                  data-tour-id="matters-table"
                  className="flex-1 min-h-0 flex flex-col"
                >
                  {isMattersLoading && allMatters.length === 0 ? (
                    <Skeleton variant="table" rows={3} />
                  ) : (
                    <Table
                      columns={columns}
                      data={tableData}
                      enableSelection
                      selectedIds={selectedMatters}
                      onSelect={(id) => setSelectedMatters(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                      onSelectAll={() => setSelectedMatters(selectedMatters.length === allMatters.length ? [] : allMatters.map(m => m.uuid || m.MatterID))}
                      onRowClick={(row) => {
                        // Only allow navigation for completed matters
                        const status = row.processing_status;
                        if (status && status !== 'completed') {
                          // Find the matter item to show proper feedback
                          const matterItem = allMatters.find((m: any) => (m.uuid || m.MatterID) === row.id);
                          if (matterItem) handleMatterClick(matterItem);
                          return;
                        }
                        history.push(`/matters/${row.uuid || row.id}`);
                      }}
                      actions={tableActions}
                      hasMore={hasMore}
                      isLoadingMore={isMattersLoading}
                      onLoadMore={loadMoreMatters}
                      sortableColumns={sortableColumns}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSortChange}
                      /*
                        The page now owns the height (see the contract above),
                        so the table fills the flex box it is in. Table's
                        default `min-h-[calc(100vh-150px)]` would force it
                        taller than that box regardless of how few rows it has
                        — the extra height being exactly the empty stretch you
                        could scroll into below the last row.
                      */
                      max="max-h-none"
                      min="min-h-0"
                      total_items={totalMatters}
                      total_showing_items={allMatters.length}
                      setIsShowing={true}
                      showingLabel={t('matters')}
                      selectedCount={selectedMatters.length}
                      resizableColumns={true}
                      defaultColumnWidths={{
                        'Name': 250,
                        'Status': 150,
                        // 'Creation Status': 150,
                        'Revenue': 140,
                        'Created At': 180,
                        'Client': 200,
                        'Access': 180,
                      }}
                      emptyState={{
                        icon: <PiBriefcaseLight />,
                        headline: t('You have no matters listed.'),
                        subtext: t('Initiate by creating a new matter to organize details, track progress, and collaborate with your team.'),
                        button: {
                          label: t('Create New Matter'),
                          onClick: handleAddMatterClick,
                        }
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
      </div>
    )}
    </div>

    {/* Audit Log Side Panel with Backdrop */}
    {showAuditLog && auditMatter && (
      <ActivityDrawer onClose={() => setShowAuditLog(false)}>
        <AuditLogPanel
          entityType="matter"
          entityId={auditMatter.id}
          entityName={auditMatter.name}
          onClose={() => {
            setShowAuditLog(false);
            setAuditMatter(null);
          }}
        />
      </ActivityDrawer>
    )}

    {/* Onboarding feature tour */}
    <FeatureTour
      steps={mattersWelcomeTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    <FeatureTour
      steps={mattersOverviewTourSteps}
      run={isOverviewTourRunning}
      onCallback={handleOverviewTourCallback}
      disableOverlay
    />
    </>
  );
};

export default Matters;
